import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { createElement } from "react";
import { getInitData } from "./telegram";
import { config } from "../config";
import { useCompany } from "./company";

interface User {
  id: string;
  role: string;
  name: string | null;
  telegramId: string | null;
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  isNewUser: boolean;
  needsOnboarding: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  authenticate: () => Promise<void>;
  logout: () => void;
  setAuthenticated: (token: string, user: User) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { company } = useCompany();
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    token: null,
    isNewUser: false,
    needsOnboarding: false,
    error: null,
  });

  const authenticate = useCallback(async () => {
    const initData = getInitData();

    if (!initData) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Not running inside Telegram",
      }));
      return;
    }
    if (!company) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Company not resolved",
      }));
      return;
    }

    try {
      const response = await fetch(config.hasuraGraphqlUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            mutation AuthenticateTelegram($input: AuthenticateTelegramInput!) {
              authenticateTelegram(input: $input) {
                success
                needsOnboarding
                token
                user {
                  id
                  role
                  name
                  telegramId
                }
                isNewUser
                error
              }
            }
          `,
          variables: {
            input: { initData, companyId: company.id },
          },
        }),
      });

      const result = await response.json();
      const data = result?.data?.authenticateTelegram;
      const gqlErr = result?.errors?.[0]?.message;

      if (data?.success && data.needsOnboarding) {
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          token: null,
          isNewUser: false,
          needsOnboarding: true,
          error: null,
        });
      } else if (data?.success && data.token) {
        setState({
          isAuthenticated: true,
          isLoading: false,
          user: data.user,
          token: data.token,
          isNewUser: data.isNewUser || false,
          needsOnboarding: false,
          error: null,
        });
      } else {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: data?.error || gqlErr || "Authentication failed",
        }));
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : "Authentication failed",
      }));
    }
  }, [company]);

  const setAuthenticated = useCallback((token: string, user: User) => {
    setState({
      isAuthenticated: true,
      isLoading: false,
      user,
      token,
      isNewUser: false,
      needsOnboarding: false,
      error: null,
    });
  }, []);

  const logout = useCallback(() => {
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      token: null,
      isNewUser: false,
      needsOnboarding: false,
      error: null,
    });
  }, []);

  useEffect(() => {
    if (company) {
      authenticate();
    }
  }, [authenticate, company]);

  return createElement(
    AuthContext.Provider,
    { value: { ...state, authenticate, logout, setAuthenticated } },
    children
  );
};
